import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Button } from '../../design-system/primitives/index.js'
import { authenticatedFetch } from '../../utils/auth.js'
import { scrollMainContentToTop } from '../../utils/scrollElementToScrollRootTop.js'
import {
  PANTRY_STARTER_EXTRAS_TAB,
  PANTRY_STARTER_FLOW_TABS,
  PANTRY_STARTER_GROUPS,
  PANTRY_STARTER_TABS,
} from '../../utils/pantryStarterCatalog.js'
import { PANTRY_STARTER_TAB_SESSION_KEY, parseJsonResponse } from './pantryHelpers.js'

const PANTRY_SETUP_TITLE = "Add what's in your pantry, fridge, and freezer."

function readSavedStarterTab() {
  try {
    const saved = sessionStorage.getItem(PANTRY_STARTER_TAB_SESSION_KEY)
    if (PANTRY_STARTER_FLOW_TABS.some((tab) => tab.key === saved)) return saved
  } catch {
    /* ignore */
  }
  return null
}

function writeSavedStarterTab(tabKey) {
  try {
    sessionStorage.setItem(PANTRY_STARTER_TAB_SESSION_KEY, tabKey)
  } catch {
    /* ignore */
  }
}

function sectionsForTab(tab) {
  const sections = []
  for (const groupId of tab.groupIds || []) {
    const group = PANTRY_STARTER_GROUPS.find((g) => g.id === groupId)
    if (group) sections.push(group)
  }
  if (tab.extraItems?.length && sections.length > 0) {
    const last = sections[sections.length - 1]
    sections[sections.length - 1] = {
      ...last,
      items: [...last.items, ...tab.extraItems],
    }
  } else if (tab.extraItems?.length) {
    sections.push({
      id: `${tab.key}-extra`,
      title: '',
      items: tab.extraItems,
    })
  }
  return sections
}

function itemsForTab(tab) {
  return sectionsForTab(tab).flatMap((section) => section.items)
}

function buildInitialChecked() {
  const m = {}
  for (const tab of PANTRY_STARTER_TABS) {
    for (const it of itemsForTab(tab)) {
      m[it.itemName] = it.defaultChecked
    }
  }
  return m
}

function buildAllUnchecked() {
  const m = {}
  for (const tab of PANTRY_STARTER_TABS) {
    for (const it of itemsForTab(tab)) {
      m[it.itemName] = false
    }
  }
  return m
}

function buildCheckedFromNames(existingNames = []) {
  const names = new Set(
    existingNames
      .map((name) => String(name || '').trim().toLowerCase())
      .filter(Boolean),
  )
  if (names.size === 0) return buildInitialChecked()
  const next = buildAllUnchecked()
  for (const tab of PANTRY_STARTER_TABS) {
    for (const item of itemsForTab(tab)) {
      next[item.itemName] = [item.itemName, ...(item.aliases || [])]
        .some((name) => names.has(name.toLowerCase()))
    }
  }
  return next
}

function firstEmptyTabKey(checked) {
  return PANTRY_STARTER_TABS.find((tab) => (
    itemsForTab(tab).every((item) => !checked[item.itemName])
  ))?.key || PANTRY_STARTER_TABS[0].key
}

function canonicalStarterName(raw) {
  const trimmed = String(raw || '').trim()
  const key = trimmed.toLowerCase()
  if (!key) return ''
  for (const tab of PANTRY_STARTER_TABS) {
    for (const item of itemsForTab(tab)) {
      if ([item.itemName, ...(item.aliases || [])].some((name) => name.toLowerCase() === key)) {
        return item.itemName
      }
    }
  }
  return trimmed
}

/**
 * Starter pantry flow: one group at a time. The primary button names the next group.
 * @param {{
 *   onSkipForNow?: () => void,
 *   onBatchSuccess: (payload: { added: number, updated: number, itemIds: string[], totalSelected: number }) => void,
 *   variant?: 'desktop' | 'mobile',
 *   headingLevel?: 1 | 2,
 *   existingItemNames?: string[],
 *   extrasContent?: import('react').ReactNode | ((api: { addExtraItems: (names: string[]) => void }) => import('react').ReactNode),
 * }} props
 */
export default function PantryQuickAddTabs({
  onSkipForNow,
  onBatchSuccess,
  variant = 'desktop',
  headingLevel = 2,
  existingItemNames = [],
  extrasContent = null,
}) {
  const [checked, setChecked] = useState(() => buildCheckedFromNames(existingItemNames))
  const [activeTab, setActiveTab] = useState(() => {
    const saved = readSavedStarterTab()
    if (saved) return saved
    return existingItemNames.length
      ? firstEmptyTabKey(buildCheckedFromNames(existingItemNames))
      : PANTRY_STARTER_TABS[0].key
  })
  const seededFromPantryRef = useRef(existingItemNames.length > 0)
  const touchedRef = useRef(false)
  useEffect(() => {
    if (seededFromPantryRef.current || touchedRef.current || existingItemNames.length === 0) return
    seededFromPantryRef.current = true
    const next = buildCheckedFromNames(existingItemNames)
    setChecked(next)
    if (!readSavedStarterTab()) setActiveTab(firstEmptyTabKey(next))
  }, [existingItemNames])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const selectedCount = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked],
  )
  const groupIndex = Math.max(
    0,
    PANTRY_STARTER_FLOW_TABS.findIndex((tab) => tab.key === activeTab),
  )
  const isLastGroup = groupIndex >= PANTRY_STARTER_FLOW_TABS.length - 1
  const nextGroup = isLastGroup ? null : PANTRY_STARTER_FLOW_TABS[groupIndex + 1]
  const extrasKey = PANTRY_STARTER_EXTRAS_TAB.key
  const isExtrasTab = activeTab === extrasKey

  const currentTab = PANTRY_STARTER_FLOW_TABS.find((t) => t.key === activeTab) || PANTRY_STARTER_TABS[0]
  const currentSections = useMemo(
    () => (isExtrasTab ? [] : sectionsForTab(currentTab)),
    [currentTab, isExtrasTab],
  )
  const singleSection = currentSections.length === 1 ? currentSections[0] : null

  const skipScrollRef = useRef(true)
  useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false
      return undefined
    }
    scrollMainContentToTop()
    return undefined
  }, [activeTab])

  const toggle = useCallback((itemName) => {
    touchedRef.current = true
    setChecked((prev) => ({ ...prev, [itemName]: !prev[itemName] }))
  }, [])

  const addExtraItems = useCallback((names) => {
    const incoming = (Array.isArray(names) ? names : [names])
      .map((name) => canonicalStarterName(name))
      .filter(Boolean)
    if (incoming.length === 0) return []
    touchedRef.current = true
    setChecked((prev) => {
      const next = { ...prev }
      for (const name of incoming) {
        const existing = Object.keys(next).find((key) => key.toLowerCase() === name.toLowerCase())
        next[existing || name] = true
      }
      return next
    })
    return incoming
  }, [])

  const clearSelections = useCallback(() => {
    touchedRef.current = true
    setChecked(buildAllUnchecked())
  }, [])

  const selectTab = useCallback((tabKey) => {
    setActiveTab(tabKey)
    writeSavedStarterTab(tabKey)
  }, [])

  const handleAdd = async () => {
    const names = Object.entries(checked)
      .filter(([, isOn]) => isOn)
      .map(([name]) => name)
    if (names.length === 0) {
      onBatchSuccess({ added: 0, updated: 0, itemIds: [], totalSelected: 0 })
      return
    }
    setBusy(true)
    setError('')
    try {
      const res = await authenticatedFetch('/inventory/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_names: names }),
      })
      let data = {}
      try {
        data = await parseJsonResponse(res)
      } catch {
        /* handled below */
      }
      if (!res.ok) throw new Error(data.detail || 'Could not add items')
      const items = Array.isArray(data?.items) ? data.items : []
      const itemIds = items.map((i) => i?.id).filter(Boolean).map(String)
      onBatchSuccess({
        added: data?.added ?? 0,
        updated: data?.updated ?? 0,
        itemIds,
        totalSelected: names.length,
      })
    } catch (e) {
      setError(e.message || 'Couldn’t update your pantry.')
    } finally {
      setBusy(false)
    }
  }

  const handlePrimaryAction = () => {
    if (nextGroup) {
      selectTab(nextGroup.key)
      return
    }
    void handleAdd()
  }

  const goBack = () => {
    const previous = PANTRY_STARTER_FLOW_TABS[groupIndex - 1]
    if (previous) selectTab(previous.key)
  }

  const ctaLabel = nextGroup ? `Continue to ${nextGroup.label}` : 'Continue to Recipes'
  const isPageTitle = headingLevel === 1
  const heading = isExtrasTab
    ? 'Anything else?'
    : (isPageTitle ? PANTRY_SETUP_TITLE : (singleSection?.title || currentTab.label))
  const lead = isPageTitle ? '' : (singleSection?.subtitle || '')
  const TitleTag = headingLevel === 1 ? 'h1' : 'h2'

  return (
    <section
      className={`pantry-quick-add${variant === 'mobile' ? ' pantry-quick-add--mobile' : ''}${isExtrasTab ? ' pantry-quick-add--extras' : ''}${headingLevel === 1 ? ' pantry-quick-add--page-title' : ''}`}
      aria-labelledby="pantry-quick-add-heading"
    >
      <div className="pantry-quick-add__header">
        <div className="pantry-quick-add__header-top">
          <TitleTag id="pantry-quick-add-heading" className="pantry-quick-add__title">
            {heading}
          </TitleTag>
          {onSkipForNow ? (
            <button
              type="button"
              className="pantry-quick-add__close"
              onClick={onSkipForNow}
              disabled={busy}
              aria-label="Skip pantry setup for now"
            >
              ×
            </button>
          ) : null}
        </div>
        {lead ? <p className="pantry-quick-add__lead">{lead}</p> : null}
      </div>

      <div className="pantry-quick-add__surface">
      <div className="pantry-quick-add__body">
        {isExtrasTab ? (
          <div className="pantry-quick-add__extras">
            {typeof extrasContent === 'function'
              ? extrasContent({ addExtraItems })
              : extrasContent || (
                <p className="pantry-quick-add__extras-copy">
                  Add something that wasn’t on the lists, or continue.
                </p>
              )}
          </div>
        ) : (
        <div className="pantry-quick-add__sections">
          {currentSections.map((section, sectionIndex) => {
            const showHead = (isPageTitle || !singleSection) && Boolean(section.title)
            const headId = `pantry-quick-add-${section.id}`
            const showClear = sectionIndex === 0 && selectedCount > 0
            return (
              <div
                key={section.id}
                className="pantry-quick-add__group"
                role={showHead ? 'group' : undefined}
                aria-labelledby={showHead ? headId : undefined}
              >
                {showHead ? (
                  <div className="pantry-quick-add__group-head">
                    <h3 id={headId} className="pantry-quick-add__group-title u-text-card-title">{section.title}</h3>
                    {section.subtitle ? (
                      <p className="pantry-quick-add__group-subtitle">{section.subtitle}</p>
                    ) : null}
                  </div>
                ) : null}
                {showClear ? (
                  <button
                    type="button"
                    className="pantry-quick-add__clear"
                    disabled={busy}
                    onClick={clearSelections}
                  >
                    Clear selections
                  </button>
                ) : null}
                <div className="pantry-quick-add__grid">
                  {section.items.map((it) => {
                    const on = !!checked[it.itemName]
                    return (
                      <button
                        key={it.itemName}
                        type="button"
                        className={`pantry-quick-add__item${on ? ' pantry-quick-add__item--on' : ''}`}
                        onClick={() => toggle(it.itemName)}
                        aria-pressed={on}
                      >
                        <span className="pantry-quick-add__mark" aria-hidden="true">✓</span>
                        <span>{it.itemName}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
        )}
      </div>

      <div className="pantry-quick-add__footer">
        <div className="pantry-quick-add__actions">
          {groupIndex > 0 ? (
            <Button
              type="button"
              variant="ghost"
              className="pantry-quick-add__back"
              disabled={busy}
              onClick={goBack}
            >
              <i className="fas fa-chevron-left" aria-hidden="true" />
              Back
            </Button>
          ) : null}
          <Button
            type="button"
            variant="primary"
            className="pantry-quick-add__cta"
            disabled={busy}
            onClick={handlePrimaryAction}
            aria-label={busy ? undefined : `${ctaLabel}, ${selectedCount} items selected so far`}
          >
            {busy ? 'Saving…' : ctaLabel}
            {busy ? null : (
              <span className="button-count" aria-hidden="true">{selectedCount} total</span>
            )}
          </Button>
          {onSkipForNow && groupIndex === 0 && selectedCount === 0 ? (
            <Button
              type="button"
              variant="ghost"
              className="pantry-quick-add__skip"
              disabled={busy}
              onClick={onSkipForNow}
            >
              Not now
            </Button>
          ) : null}
        </div>
      </div>
      </div>

      {error ? <p className="pantry-inline-error">{error}</p> : null}
    </section>
  )
}
